import { gsap } from 'gsap';
import { Sprite, type Texture } from 'pixi.js';
import type { Actor } from '../../battle/types';
import { DESIGN_HEIGHT } from '../../data/constants';
import type { ArenaPlayContext } from '../types';

// Y of the embedded ball in design-px - lodged in the upper wall area, well
// above character bodies and torch heads. Tune if arena art changes.
const EMBED_Y = 150;
// Horizontal offset from the defender's baseX, in design-px. Sign is relative
// to the defender's facing - positive = away from arena center (toward the
// outer wall on the defender's side), negative = toward center. 0 = directly
// above the defender's centerline.
const EMBED_X_OFFSET = 120;
// Visual size for the CLEAN cannonball sprite (in flight phase, falling).
// MUST match Cannonball.TARGET_SIZE in src/skills/SparkyBot/Cannon/Cannonball.ts -
// on block the flying Cannonball is disposed at impact and a fresh embedded
// sprite spawns at the same spot. Mismatched sizes cause a visible "pop" at
// the swap. If you tweak one, tweak the other.
const EMBED_SIZE = 25;
// Visual size for WALL-ART sprites (cannonball-in-wall, damaged-wall). These
// textures contain the ball PLUS surrounding wall damage (cracks, debris), so
// fitting them to EMBED_SIZE would make the ball portion appear tiny. Tune
// independently from EMBED_SIZE so the ball portion of the in-wall sprite
// looks the same visual size as the clean ball.
const LODGED_SIZE = 90;
// Random tilt range so the ball reads as "stuck at an angle" instead of
// perfectly upright.
const EMBED_MAX_TILT_RAD = 0.4;
// Fly-in duration when spawn is given a fromPosition (e.g. cannonball impact).
// Keep short - this is the tail of the cannon's ricochet, not a fresh trip.
const FLYIN_DURATION = 0.035;
const FLYIN_SPIN_RAD = Math.PI;

// Drop trajectory: a single accelerating fall (gravity-like, never decelerates)
// with a brief sideways drift at the start - ball nudges out of the wall hole
// and then just falls. Y uses one continuous power2.in over the whole drop so
// there's no velocity reset mid-fall. X drifts in parallel during the first
// portion only. Durations are in BATTLE-pace seconds (gsap globalTimeline
// timeScale slows them).
const DROP_TOTAL_DURATION = 0.32;
// Fraction of the total fall during which the sideways drift happens. After
// this, X is locked and only Y continues accelerating downward. Tuned together
// with TOTAL so the roll-out keeps its old pace (~0.19s) while the fall itself
// is faster - reads as real gravity, not a feather drop.
const DROP_DRIFT_FRAC = 0.6;
// Horizontal drift in design-px. Sign is flipped per side via defender.facing
// so the ball drifts INWARD (toward arena center, away from the wall it came
// out of) regardless of which side it was lodged on - reads as the ball
// scattering into the open arena rather than further into the wall.
const DROP_SIDEWAYS_DRIFT = 5;
// Nudge applied to the clean falling ball's start position (in design-px).
// The ball inherits ball.x/ball.y from the lodged sprite which is anchored at
// the texture center, but the artist may have drawn the ball off-center within
// the wall-art texture. Tune so the clean ball starts at the same spot the
// painted ball appeared inside the in-wall sprite. X is mirrored per side via
// defender.facing - positive = INWARD (toward arena center), negative = OUTWARD.
const DROP_START_OFFSET_X = 20;
const DROP_START_OFFSET_Y = 10;
const DROP_ROTATION_RAD = Math.PI * 1.5;
// Floor Y for the dropped ball. Matches BattleScene's character baseline so
// the ball lands on the same ground plane characters stand on.
const GROUND_Y = DESIGN_HEIGHT - 75;
// BAM! burst tuning for the wall-drop blast (size in design px, speed in
// frames/tick). Independent from the direct cannon-hit burst.
const BAM_SIZE = 220;
const BAM_SPEED = 0.9;
// Wall-break blast (second ball into the same wall) - the biggest bang of the
// three, it blows a hole in the wall.
const BREAK_BAM_SIZE = 260;

// One ball per side. Single-battle assumption matches the rest of the engine.
// Module-scoped so attach in one event and release in a later event find each
// other without threading state through ArenaPlayContext.
const embedded: Partial<Record<Actor, Sprite>> = {};

// A released ball that is falling (or resting on the ground) and waiting for
// its bang. The explosion is a rendezvous: the drop resolves `landed` at
// touchdown, and the arena_explode routing claims the ball and fires the BAM +
// (renderer-side) pain at the same moment. If nothing ever claims it (e.g. the
// target died so the engine emitted no explosion hit), a short fuse blows the
// ball up on its own.
type DroppedBall = {
  ball: Sprite;
  landed: Promise<{ x: number; y: number }>;
  claimed: boolean;
  fuseTimer?: number;
};
const dropped: Partial<Record<Actor, DroppedBall>> = {};

// Damage marks left in the wall by dropped balls, per side. Tracked so a wall
// break can wipe them together with the lodged ball.
const marks: Partial<Record<Actor, Sprite[]>> = {};

// Walls blown open by a second embedded ball (arena_break). A broken wall
// holds nothing - spawnEmbeddedBall no-ops there (the cannon's direct attach
// call doesn't know the wall state, so the guard lives here).
const brokenWalls: Record<Actor, boolean> = { p1: false, p2: false };

// Wall-time fuse for an unclaimed grounded ball (see DroppedBall).
const UNCLAIMED_FUSE_MS = 1200;

// Clears the per-battle registry. Called from the arena's mount() so a fresh
// battle never inherits a stale (already destroyed) ball from a previous one -
// battles are restartable from the desktop screen now.
export function resetEmbeddedBalls(): void {
  embedded.p1 = undefined;
  embedded.p2 = undefined;
  for (const side of ['p1', 'p2'] as const) {
    const record = dropped[side];
    if (record?.fuseTimer !== undefined) clearTimeout(record.fuseTimer);
    dropped[side] = undefined;
    marks[side] = undefined;
    brokenWalls[side] = false;
  }
}

// Spawns an embedded cannonball over the given side. Idempotent for the
// engine-emitted arena_attach (no fromPosition): if a ball is already lodged
// on that side, returns immediately (that event may fire after a skill has
// already placed the ball - we want the skill's path to win). A skill call
// WITH fromPosition is a real new shot even when a ball is lodged (second
// blocked cannon replaces the old one) - the fly-in plays and the old sprite
// is removed at the moment of wall contact.
//
// If `fromPosition` is given, the ball starts there with the flight texture
// and animates into the wall pose (used by cannon to make the ricochet read
// as the ball flying into the wall). Without it, the ball appears directly
// at its final position.
//
// If `lodgedTexture` is given, the sprite swaps to it the moment the ball
// reaches the wall (after fly-in, or immediately on direct spawn) - lets the
// "stuck in wall" art (cracks, partial embed) replace the in-flight cannonball.
export async function spawnEmbeddedBall(
  side: Actor,
  texture: Texture,
  ctx: ArenaPlayContext,
  opts?: {
    fromPosition?: { x: number; y: number };
    lodgedTexture?: Texture;
  },
): Promise<void> {
  // Broken wall - nothing to lodge into. Covers the cannon's direct attach
  // call on a blocked shot after the wall on this side already blew open.
  if (brokenWalls[side]) return;
  const previous = embedded[side];
  if (previous && !opts?.fromPosition) return;

  // Initial texture: flight sprite if there's a fly-in to play, else jump
  // straight to the lodged pose (no in-flight phase to render). Picks the
  // matching size since flight and lodged sprites have different scales.
  const startsLodged = !opts?.fromPosition && !!opts?.lodgedTexture;
  const initialTexture = startsLodged ? opts.lodgedTexture! : texture;
  const ball = new Sprite(initialTexture);
  ball.anchor.set(0.5);
  fitTexture(ball, initialTexture, startsLodged ? LODGED_SIZE : EMBED_SIZE);
  // Lodged-art textures are drawn for the LEFT wall - mirror on the right.
  // Clean cannonball is symmetric so the mirror is a visual no-op there;
  // applying unconditionally on lodged-only branch keeps the logic obvious.
  if (startsLodged) applyWallMirror(ball, side);

  const defender = ctx.scene.getSprite(side);
  // facing is +1 for p1 (faces right -> outer wall is to the LEFT, i.e.
  // negative-X), -1 for p2 (faces left -> outer wall is to the RIGHT). So the
  // "outward" direction is -facing.
  const finalX = defender.baseX + EMBED_X_OFFSET * -defender.facing;
  const finalY = EMBED_Y;
  const finalRotation = (Math.random() - 0.5) * 2 * EMBED_MAX_TILT_RAD;

  if (opts?.fromPosition) {
    ball.position.set(opts.fromPosition.x, opts.fromPosition.y);
    ball.rotation = 0;
  } else {
    ball.position.set(finalX, finalY);
    ball.rotation = finalRotation;
  }

  ctx.scene.spawnEffect(ball);
  embedded[side] = ball;

  if (opts?.fromPosition) {
    await new Promise<void>((resolve) => {
      gsap.to(ball, {
        x: finalX,
        y: finalY,
        rotation: finalRotation + FLYIN_SPIN_RAD,
        duration: FLYIN_DURATION,
        // Linear - ball flies into the wall at constant speed and stops dead
        // on contact (no decel easing that would read as "slowing down before
        // sticking").
        ease: 'none',
        onComplete: resolve,
      });
    });
    // Second block on this side: the new ball takes the old one's spot in the
    // wall - remove the previous sprite right at contact so there's never two.
    if (previous && !previous.destroyed) {
      previous.parent?.removeChild(previous);
      previous.destroy();
    }
    // After fly-in: swap to the "stuck in wall" sprite (if provided) and drop
    // the spin contribution so the lodged art settles at its natural tilt.
    // Lodged sprite uses LODGED_SIZE - it's a wall-art texture (ball + cracks).
    if (opts.lodgedTexture) {
      ball.texture = opts.lodgedTexture;
      fitTexture(ball, opts.lodgedTexture, LODGED_SIZE);
      applyWallMirror(ball, side);
      ball.rotation = finalRotation;
    }
  }
}

function fitTexture(sprite: Sprite, texture: Texture, targetSize: number): void {
  const longest = Math.max(texture.width, texture.height);
  if (longest > 0) sprite.scale.set(targetSize / longest);
}

// Wall-art textures (cannonball-in-wall, damaged-wall) are drawn for the LEFT
// wall orientation. Mirror horizontally on the right side so asymmetric
// details (crack directions, etc.) look natural for both. MUST be called AFTER
// fitTexture - flips the sign of existing scale.x.
function applyWallMirror(sprite: Sprite, side: Actor): void {
  if (side === 'p2') sprite.scale.x = -sprite.scale.x;
}

// Drops the embedded ball: sideways drift out of the wall hole, then an
// accelerating fall to the ground, where it RESTS until the explosion is
// claimed (see DroppedBall / explodeDroppedBall). Idempotent - if no ball is
// embedded on this side, returns immediately. Resolves at touchdown.
export async function dropBall(
  side: Actor,
  ctx: ArenaPlayContext,
): Promise<void> {
  const ball = embedded[side];
  if (!ball) return;
  embedded[side] = undefined;

  // Snapshot wall pose before mutating the ball - the damage mark sits at the
  // exact embed point (LODGED_SIZE matches the lodged ball it replaces; same
  // tilt for visual continuity) and stays there for the rest of the battle.
  const damagedWallTex = ctx.textures.damagedWall;
  if (damagedWallTex) {
    const mark = new Sprite(damagedWallTex);
    mark.anchor.set(0.5);
    fitTexture(mark, damagedWallTex, LODGED_SIZE);
    applyWallMirror(mark, side);
    mark.position.set(ball.x, ball.y);
    mark.rotation = ball.rotation;
    ctx.scene.spawnEffect(mark);
    (marks[side] ??= []).push(mark);
    // Re-add ball so it renders ABOVE the new mark while falling (spawnEffect
    // adds to the top of particleLayer; calling it on an existing child moves
    // it back to the top).
    ctx.scene.spawnEffect(ball);
  }

  // What falls is the clean cannonball (EMBED_SIZE), not the "stuck in wall"
  // art - the lodged-version stays imprinted as the damage mark above.
  // Rotation kept for visual continuity. Position gets a tunable nudge so the
  // clean (small) ball starts where the painted ball actually was inside the
  // wall-art sprite, not at the texture's geometric center.
  const cleanTex = ctx.textures.cannonball;
  if (cleanTex) {
    ball.texture = cleanTex;
    fitTexture(ball, cleanTex, EMBED_SIZE);
  }

  // Drop is anchored to the ball's current (wall) position, not the defender.
  // facing picks the INWARD direction so the ball drifts toward arena center
  // (away from the wall it came out of) - reads as scattering into open space.
  const inwardSign = ctx.scene.getSprite(side).facing;
  ball.x += DROP_START_OFFSET_X * inwardSign;
  ball.y += DROP_START_OFFSET_Y;
  const startX = ball.x;
  const landingX = startX + DROP_SIDEWAYS_DRIFT * inwardSign;
  const landingY = GROUND_Y;
  const driftDuration = DROP_TOTAL_DURATION * DROP_DRIFT_FRAC;

  const landed = new Promise<{ x: number; y: number }>((resolve) => {
    const tl = gsap.timeline({ onComplete: () => resolve({ x: landingX, y: landingY }) });
    // Y: one continuous accelerating fall (gravity). Never decelerates - the
    // ball only picks up speed from start to landing.
    tl.to(
      ball,
      { y: landingY, duration: DROP_TOTAL_DURATION, ease: 'power2.in' },
      0,
    );
    // X: short sideways drift during the first portion of the fall. Linear so
    // it doesn't decelerate either. Runs in parallel with Y.
    tl.to(
      ball,
      { x: landingX, duration: driftDuration, ease: 'none' },
      0,
    );
    // Spin in parallel for the whole drop - matches the Y accel curve.
    tl.to(
      ball,
      { rotation: ball.rotation + DROP_ROTATION_RAD, duration: DROP_TOTAL_DURATION, ease: 'power2.in' },
      0,
    );
  });

  // Register BEFORE awaiting so an arena_explode arriving mid-fall can claim
  // the ball and rendezvous on `landed`.
  const record: DroppedBall = { ball, landed, claimed: false };
  dropped[side] = record;

  const landing = await landed;
  // Unclaimed after touchdown (target already dead -> engine emitted no hit,
  // or the explode event is still queued behind other renders): light a short
  // fuse. The claim path clears it and fires the bang itself.
  if (!record.claimed) {
    record.fuseTimer = window.setTimeout(() => {
      explodeNow(side, record, ctx, landing.x, landing.y);
    }, UNCLAIMED_FUSE_MS);
  }
}

// Claims the dropped ball for the arena_explode event: waits out the rest of
// the fall (if still airborne), then fires the bang. The renderer awaits this
// BEFORE playing pain reactions, so the BAM and pain-air start together.
// No-op when nothing was dropped on this side.
export async function explodeDroppedBall(
  side: Actor,
  ctx: ArenaPlayContext,
): Promise<void> {
  const record = dropped[side];
  if (!record) return;
  record.claimed = true;
  if (record.fuseTimer !== undefined) clearTimeout(record.fuseTimer);
  const landing = await record.landed;
  explodeNow(side, record, ctx, landing.x, landing.y);
}

// True while a ball is lodged in this side's wall. The arena's attach routing
// uses it to divert a second ricochet into the crash-and-break path.
export function hasEmbeddedBall(side: Actor): boolean {
  return !!embedded[side];
}

// Second ball ricochets into an occupied wall: it never lodges (no in-wall
// art at all) - the clean ball flies to the embed point and the wall blows
// the moment it connects. Called by the arena's attach routing at the
// ricochet; the engine's arena_break that arrives later finds the wall
// already broken and no-ops.
export async function crashBallIntoWall(
  side: Actor,
  texture: Texture,
  ctx: ArenaPlayContext,
  fromPosition: { x: number; y: number },
): Promise<void> {
  if (brokenWalls[side]) return;
  const ball = new Sprite(texture);
  ball.anchor.set(0.5);
  fitTexture(ball, texture, EMBED_SIZE);
  ball.position.set(fromPosition.x, fromPosition.y);
  ctx.scene.spawnEffect(ball);

  const defender = ctx.scene.getSprite(side);
  const finalX = defender.baseX + EMBED_X_OFFSET * -defender.facing;
  await new Promise<void>((resolve) => {
    gsap.to(ball, {
      x: finalX,
      y: EMBED_Y,
      rotation: FLYIN_SPIN_RAD,
      duration: FLYIN_DURATION,
      ease: 'none',
      onComplete: resolve,
    });
  });

  if (!ball.destroyed) {
    ball.parent?.removeChild(ball);
    ball.destroy();
  }
  breakWall(side, ctx);
}

// Wall break (arena_break): a second ball hit the same wall and blew it open.
// Wipes ALL wall art on that side (the lodged ball + any damage marks), fires
// the big BAM at the embed point and flags the wall so later blocked cannons
// have nothing to lodge into. Idempotent - once the wall is broken (e.g. the
// crash path already fired at the ricochet), a repeat call returns silently.
export function breakWall(side: Actor, ctx: ArenaPlayContext): void {
  if (brokenWalls[side]) return;
  brokenWalls[side] = true;
  const ball = embedded[side];
  embedded[side] = undefined;
  // BAM at the lodged ball's actual spot; fall back to the standard embed
  // point if the ball sprite is missing (e.g. its texture failed to load).
  const defender = ctx.scene.getSprite(side);
  const hasBall = !!ball && !ball.destroyed;
  const bamX = hasBall ? ball.x : defender.baseX + EMBED_X_OFFSET * -defender.facing;
  const bamY = hasBall ? ball.y : EMBED_Y;
  if (hasBall) {
    // The fly-in tween of the just-arrived second ball may still be running.
    gsap.killTweensOf(ball);
    ball.parent?.removeChild(ball);
    ball.destroy();
  }
  for (const mark of marks[side] ?? []) {
    if (mark.destroyed) continue;
    mark.parent?.removeChild(mark);
    mark.destroy();
  }
  marks[side] = undefined;
  ctx.scene.spawnBam(bamX, bamY, { size: BREAK_BAM_SIZE, speed: BAM_SPEED });
}

// Removes the grounded ball and fires the shared BAM! burst at the landing
// point. Guarded for double-calls (fuse vs claim race) and scene teardown.
function explodeNow(
  side: Actor,
  record: DroppedBall,
  ctx: ArenaPlayContext,
  x: number,
  y: number,
): void {
  if (dropped[side] !== record) return; // already exploded or battle reset
  dropped[side] = undefined;
  if (record.ball.destroyed) return; // scene torn down - nothing to blow up
  record.ball.parent?.removeChild(record.ball);
  record.ball.destroy();
  // Fire-and-forget (self-destructs); the renderer plays the victim's pain
  // reaction over it, same overlap as a direct cannon hit.
  ctx.scene.spawnBam(x, y, { size: BAM_SIZE, speed: BAM_SPEED });
}
